/**
 * @design-contract CUR-113 + CUR-206
 *
 * Renders a progress-message ("thinking") cluster. The slash-commands
 * PR removed the lateral PMV chrome inset (was `px-[16px]`) so the
 * thinking row, leave-open hint, and expanded body now sit flush
 * against the cluster wrapper. Only the thinking-state title row
 * keeps a `pl-[16px]` so the pill background lines up with the
 * decorative gradient. The cluster-end feedback row's `px-[16px]`
 * inset is owned by `ASSISTANT_CLUSTER_END_INSET_CLASS` in the
 * design module.
 */
import React, { useEffect, useRef, useState } from 'react'
import { ProgressMessageData, ProgressMessageTodoItem } from '~/contexts/GlobalStreamingContext'
import { TodoList } from '~/components/task/TodoList'
import ProgressMarkdown from '~/components/common/ProgressMarkdown'
import { stripMarkdown } from '~/utils/stripMarkdown'

// Store expanded state per message type to persist across updates during streaming
// During streaming, progress messages replace each other (same type, different hash)
// So we track by messageType to maintain expanded state across replacements
const expandedStateMap = new Map<string, boolean>()

interface ChatProgressMessageViewProps {
  progressMessages: ProgressMessageData[] // Array of all progress messages in the stream
  isStreaming?: boolean // Whether thinking/streaming is currently ongoing
  taskContext?: ProgressTaskContext
}

interface ProgressTaskContext {
  taskId: number
  taskTitle?: string
  statusLabel?: string
  isRunning?: boolean
  createdTsMs?: number
  updatedTsMs?: number
}

// Helper to detect if a string looks like a file name (for legacy internalMsgs fallback)
const isFileName = (msg: string): boolean => {
  const trimmed = msg.trim()
  return /^[\w\-./]+\.\w+$/.test(trimmed) || /^[\w\-./\\]+$/.test(trimmed)
}

const normalizeProgressTimestampMs = (value: number | undefined): number | undefined => {
  if (!value || value <= 0) return undefined
  if (value > 10_000_000_000_000_000) return value / 1_000_000
  if (value > 100_000_000_000_000) return value / 1_000
  if (value < 10_000_000_000) return value * 1_000
  return value
}

// Some assistants build summary titles like "Worked for 423s" server-side
// and persist them verbatim in the task DB, which becomes unreadable once
// the agent runs more than a minute. Rewrite a trailing `{N}s` token at
// render time so existing rows render the same friendly format as new
// emissions, without a backend migration. Anchored to end-of-string so
// titles like "Used 10s of files" are never touched. (CUR-177)
const TRAILING_SECONDS_RE = /\b(\d+)s(\s*)$/

export const formatProgressTitle = (title: string | undefined | null): string => {
  if (!title) return ''
  const match = TRAILING_SECONDS_RE.exec(title)
  if (!match) return title
  const total = Math.max(0, Math.floor(Number(match[1])))
  if (!Number.isFinite(total)) return title

  let formatted: string
  if (total < 60) {
    formatted = `${total}s`
  } else if (total < 3600) {
    const m = Math.floor(total / 60)
    const s = total % 60
    formatted = s === 0 ? `${m}m` : `${m}m ${s}s`
  } else {
    const h = Math.floor(total / 3600)
    const m = Math.floor((total % 3600) / 60)
    formatted = m === 0 ? `${h}h` : `${h}h ${m}m`
  }
  return title.replace(TRAILING_SECONDS_RE, `${formatted}${match[2]}`)
}

interface ChatProgressMessageViewInnerProps {
  sortedMessages: ProgressMessageData[]
  stateKey: string
  isStreaming: boolean
  hasSummary: boolean
  buttonTextMessage: ProgressMessageData
  nonTodoRegularMessages: ProgressMessageData[]
  taskContext?: ProgressTaskContext
}

function ChatProgressMessageViewInner({
  sortedMessages,
  stateKey,
  isStreaming,
  hasSummary,
  buttonTextMessage,
  nonTodoRegularMessages,
  taskContext
}: ChatProgressMessageViewInnerProps) {
  const {
    title: latestTitle,
    messageType: latestMessageType,
    msgHash: latestMsgHash = ''
  } = buttonTextMessage

  const isThinking = isStreaming && latestMessageType !== 'SUMMARY'
  const messageTypeOf = (msg: ProgressMessageData): unknown => {
    const msgAny = msg as unknown as Record<string, unknown>
    return msg.messageType || msgAny.message_type
  }

  const distinctNonSummaryMessages = (() => {
    const seen = new Set<string>()
    return sortedMessages
      .filter(msg => messageTypeOf(msg) !== 'SUMMARY' && !!msg.msgHash && !!msg.title)
      .filter(msg => {
        if (seen.has(msg.msgHash || '')) return false
        seen.add(msg.msgHash || '')
        return true
      })
  })()

  const messagesToDisplay = isThinking
    ? [...distinctNonSummaryMessages]
        .filter(msg => msg.msgHash !== latestMsgHash)
        .sort((a, b) => (b.createdTsNs || 0) - (a.createdTsNs || 0))
    : sortedMessages
  const expandable = isThinking
    ? distinctNonSummaryMessages.length > 1
    : messagesToDisplay.length > 0
  const activityTimestamps = [buttonTextMessage, ...distinctNonSummaryMessages]
    .map(msg => normalizeProgressTimestampMs(msg.updatedTsNs ?? msg.createdTsNs))
    .filter((value): value is number => typeof value === 'number')
  const taskFallbackActivityMs = taskContext?.updatedTsMs ?? taskContext?.createdTsMs
  const latestActivityMs =
    activityTimestamps.length > 0
      ? Math.max(...activityTimestamps)
      : (taskFallbackActivityMs ?? Date.now())
  const [nowMs, setNowMs] = useState(() => Date.now())
  const taskContextTaskId = taskContext?.taskId
  useEffect(() => {
    if (!isThinking || taskContextTaskId === undefined) return
    setNowMs(Date.now())
    const interval = window.setInterval(() => setNowMs(Date.now()), 1_000)
    return () => window.clearInterval(interval)
  }, [isThinking, taskContextTaskId])

  const waitingMs = isThinking && taskContext ? Math.max(0, nowMs - latestActivityMs) : 0
  const isWaitingOnTask = waitingMs >= 30_000
  const showLeaveOpenHint = waitingMs >= 120_000
  const taskStatusText =
    taskContext && isThinking
      ? isWaitingOnTask
        ? `Still waiting for task #${taskContext.taskId}...`
        : `Task #${taskContext.taskId} is running`
      : ''
  const shouldUseTaskStatusTitle =
    isThinking && !!taskContext && latestMsgHash === 'thinking-fallback' && !!taskStatusText
  // Reformat trailing raw-seconds suffixes ("Worked for 423s" → "Worked
  // for 7m 3s") for display only — the backend persists titles verbatim,
  // so we rewrite at render time so old DB rows render the same way as
  // new ones. (CUR-177)
  const displayTitle = formatProgressTitle(shouldUseTaskStatusTitle ? taskStatusText : latestTitle)
  const strippedLatestTitle = stripMarkdown(formatProgressTitle(latestTitle))

  const finalRegularMsgHash = (() => {
    if (isThinking) return latestMsgHash
    const lastByCreated = nonTodoRegularMessages.reduce<ProgressMessageData | null>((acc, cur) => {
      if (!acc) return cur
      const accTs = acc.createdTsNs ?? 0
      const curTs = cur.createdTsNs ?? 0
      return curTs >= accTs ? cur : acc
    }, null)
    return lastByCreated?.msgHash || ''
  })()

  const clusterHash = sortedMessages.length > 0 ? sortedMessages[0].msgHash || 'default' : 'default'
  const gradientId = `chevron-gradient-${clusterHash}`
  const clipPathId = `clip0-${clusterHash}`
  const shimmerAnimationId = `shimmer-${clusterHash}`

  const [isExpanded, setIsExpanded] = useState(() => {
    // When the stream is finished (SUMMARY exists), default to collapsed.
    // This avoids reading a stale "expanded=true" from a prior key/mount.
    if (!isStreaming && hasSummary) {
      return false
    }
    return expandedStateMap.get(stateKey) ?? false
  })
  const showLatestActivitySubtitle =
    isThinking &&
    isExpanded &&
    !!taskContext &&
    !!strippedLatestTitle &&
    strippedLatestTitle !== stripMarkdown(displayTitle)

  // Sync expanded state when stateKey changes (in case component is reused)
  useEffect(() => {
    // After streaming is finished, do not re-sync from the persistent map.
    // This prevents "collapse then re-expand" flicker if the stateKey changes.
    if (!isStreaming && hasSummary) {
      return
    }
    const storedState = expandedStateMap.get(stateKey)
    if (storedState !== undefined && storedState !== isExpanded) {
      setIsExpanded(storedState)
    }
  }, [stateKey, isStreaming, hasSummary, isExpanded])

  // Collapse once when streaming ends (SUMMARY exists) and persist that for this key.
  // This prevents any previously-expanded state from resurfacing after finish.
  const prevIsStreamingRef = useRef<boolean>(isStreaming)
  useEffect(() => {
    const wasStreaming = prevIsStreamingRef.current
    const finishedNow = wasStreaming && !isStreaming
    if (finishedNow && hasSummary) {
      expandedStateMap.set(stateKey, false)
      if (isExpanded) {
        setIsExpanded(false)
      }
    }
    prevIsStreamingRef.current = isStreaming
  }, [isStreaming, hasSummary, stateKey, isExpanded])

  // Update both local state and persistent map when toggling
  const handleToggle = () => {
    const newExpanded = !isExpanded
    setIsExpanded(newExpanded)
    expandedStateMap.set(stateKey, newExpanded)
  }

  // Determine button text gradient style based on message type
  // When thinking, add a shimmer effect that sweeps across the text (ChatGPT-style)
  // CUR-206: completed (non-streaming) PMVs use the same neutral dark gray as
  // SUMMARY, regardless of whether they hold a SUMMARY message. The purple→
  // pink→blue gradient is reserved for the live "thinking" state — once a
  // phase is finalized, the gradient reads as visual noise relative to the
  // surrounding chat text.
  const buttonTextStyle: React.CSSProperties = !isThinking
    ? { color: 'var(--ct-text-secondary)' }
    : {
        // Shimmer effect: gradient that moves across the text (subtle highlight, not pure white)
        background:
          'linear-gradient(90deg, #AB97FE 0%, #C4B5E8 25%, #DDD8F0 50%, #C4B5E8 75%, #A8CFF1 100%)',
        backgroundSize: '200% 100%',
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        color: 'transparent',
        animation: `${shimmerAnimationId} 1.5s ease-in-out infinite`
      }

  // Render internalMsgs details
  const renderLegacyDetail = (msg: string, index: number) => {
    if (isFileName(msg)) {
      // File name - show with file icon styling
      return (
        <div
          key={`internal-${index}`}
          className='flex items-center gap-[6px] px-2 py-1 bg-tokens-neutral-bg-grey-2 rounded-md'
        >
          <svg width='14' height='14' viewBox='0 0 16 16' fill='none' className='flex-shrink-0'>
            <path
              d='M13.5 4.5V13.5C13.5 13.7652 13.3946 14.0196 13.2071 14.2071C13.0196 14.3946 12.7652 14.5 12.5 14.5H3.5C3.23478 14.5 2.98043 14.3946 2.79289 14.2071C2.60536 14.0196 2.5 13.7652 2.5 13.5V2.5C2.5 2.23478 2.60536 1.98043 2.79289 1.79289C2.98043 1.60536 3.23478 1.5 3.5 1.5H9.5L13.5 4.5Z'
              stroke='#8c8c8c'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
            <path
              d='M9.5 1.5V4.5H13.5'
              stroke='#8c8c8c'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
          <span className='font-medium leading-normal text-[10px] font-sans'>{msg.trim()}</span>
        </div>
      )
    }
    // Regular detail text
    return (
      <span
        key={`internal-${index}`}
        className={`font-sans font-bold leading-normal text-[10px] text-tokens-neutral-text-quaternary`}
      >
        {msg}
      </span>
    )
  }

  // Always show collapsible button view
  return (
    <div
      className={`flex flex-col items-start gap-[4px] self-stretch w-full rounded-[20px] ${isThinking ? 'select-none' : ''}
      ${
        isThinking && isExpanded
          ? 'bg-[var(--status-link-base,rgba(132,111,225,0.10))] p-[24px]'
          : isThinking
            ? 'bg-[var(--status-link-base,rgba(132,111,225,0.10))] px-[24px] py-[8px]'
            : ''
      }`}
    >
      {/* Shimmer animation keyframes for thinking state (ChatGPT-style) */}
      {isThinking && (
        <style>
          {`
            @keyframes ${shimmerAnimationId} {
              0% {
                background-position: 100% 0;
              }
              100% {
                background-position: -100% 0;
              }
            }
          `}
        </style>
      )}
      <div
        role={expandable ? 'button' : undefined}
        onClick={expandable ? handleToggle : undefined}
        className={`flex items-start align-stretch ${isThinking ? 'pl-[16px] justify-between' : 'py-[8px]'} gap-[8px] overflow-hidden w-full ${expandable ? 'cursor-pointer' : ''}`}
        data-pmv-latest-message-type={latestMessageType ?? 'unknown'}
        data-pmv-latest-msg-hash={latestMsgHash || 'none'}
        data-pmv-is-thinking={String(isThinking)}
        data-pmv-cluster-size={sortedMessages.length}
      >
        {/* CUR-206: chevron sits next to the title at the title's natural
            width. The title only truncates (with ellipsis) when the row
            would otherwise overflow horizontally — so a SHORT title leaves
            blank space to the right of the chevron, and a LONG title
            shrinks until the chevron is pinned at the right edge. The
            thinking state still uses flex-[1_0_0] + max-w-xl because its
            decorative pill background needs to span the full width.
              - Non-thinking: NO flex-grow on the inner div (so it doesn't
                fill the row); `min-w-0` lets it shrink below its content's
                intrinsic width; `max-w-full` on the span scopes the
                ellipsis to the available shrunk width.
              - Row uses `justify-start` (vs `justify-between` while
                thinking) so the chevron flows immediately after the title
                rather than getting pushed to the far right unconditionally.
        */}
        <div
          className={`flex flex-col justify-center items-start ${isThinking ? 'flex-[1_0_0]' : 'min-w-0'}`}
        >
          <span
            className={`text-[14px] font-medium leading-[22px] font-sans overflow-hidden whitespace-nowrap text-ellipsis ${isThinking ? 'text-center shrink-0 max-w-xl' : 'text-left max-w-full'}`}
            style={buttonTextStyle}
          >
            {stripMarkdown(displayTitle)}
          </span>
          {showLatestActivitySubtitle && (
            <span className='max-w-xl overflow-hidden text-ellipsis whitespace-nowrap font-mono text-[11px] font-semibold leading-[18px] text-tokens-neutral-text-tertiary'>
              {strippedLatestTitle}
            </span>
          )}
        </div>

        {expandable && (
          <div
            className={`flex w-[20px] h-[20px] gap-[10px] self-center justify-center items-center overflow-hidden rounded-[24px] ${isThinking ? 'bg-[var(--status-link-base,rgba(0,0,0,0.06))]' : ''}`}
          >
            <svg
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
              className='w-4 h-4 text-tokens-neutral-text-secondary'
              style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
            >
              <defs>
                {/* CUR-206: gradient chevron is reserved for the live thinking
                  state. Completed PMVs (with or without a SUMMARY message
                  inside) render the chevron solid dark so the row reads with
                  the same visual weight as the surrounding completed PMVs. */}
                {isThinking && (
                  <linearGradient id={gradientId} x1='0%' y1='0%' x2='100%' y2='0%'>
                    <stop offset='0%' stopColor='#AB97FE' />
                    <stop offset='82%' stopColor='#D3B6B8' />
                    <stop offset='98.98%' stopColor='#A8CFF1' />
                  </linearGradient>
                )}
                <clipPath id={clipPathId}>
                  <rect width='16' height='16' fill='white' />
                </clipPath>
              </defs>
              <g clipPath={`url(#${clipPathId})`}>
                <path
                  d='M11.354 8.35403L6.35403 13.354C6.30757 13.4005 6.25242 13.4373 6.19173 13.4625C6.13103 13.4876 6.06598 13.5006 6.00028 13.5006C5.93458 13.5006 5.86953 13.4876 5.80883 13.4625C5.74813 13.4373 5.69298 13.4005 5.64653 13.354C5.60007 13.3076 5.56322 13.2524 5.53808 13.1917C5.51294 13.131 5.5 13.066 5.5 13.0003C5.5 12.9346 5.51294 12.8695 5.53808 12.8088C5.56322 12.7481 5.60007 12.693 5.64653 12.6465L10.2934 8.00028L5.64653 3.35403C5.55271 3.26021 5.5 3.13296 5.5 3.00028C5.5 2.8676 5.55271 2.74035 5.64653 2.64653C5.74035 2.55271 5.8676 2.5 6.00028 2.5C6.13296 2.5 6.26021 2.55271 6.35403 2.64653L11.354 7.64653C11.4005 7.69296 11.4374 7.74811 11.4626 7.80881C11.4877 7.86951 11.5007 7.93457 11.5007 8.00028C11.5007 8.06599 11.4877 8.13105 11.4626 8.19175C11.4374 8.25245 11.4005 8.30759 11.354 8.35403Z'
                  fill={isThinking ? `url(#${gradientId})` : 'currentColor'}
                />
              </g>
            </svg>
          </div>
        )}
      </div>

      {isThinking && isExpanded && taskContext && showLeaveOpenHint && (
        <div className='flex w-full flex-wrap items-center gap-2 py-[2px] text-[12px] leading-[18px] text-tokens-neutral-text-tertiary'>
          <span className='min-w-0 flex-1'>
            You can leave this chat open; results will appear here.
          </span>
        </div>
      )}

      {isExpanded && expandable && (
        <div
          className={`flex ${isThinking ? 'pt-[2px]' : ''} gap-[8px] items-start self-stretch w-full`}
        >
          {/* Vertical Divider Line */}
          {!isThinking && (
            <div className='flex px-[8px] items-start self-stretch'>
              <div className='w-px bg-[rgba(0,0,0,0.06)] self-stretch' />
            </div>
          )}

          {/* Content - Mini stream of all progress messages in chronological order */}
          <div
            className={`flex flex-col font-mono items-start gap-[8px] ${!isThinking ? 'py-[4px]' : ''} self-stretch w-full`}
          >
            {messagesToDisplay.map((msg, msgIndex) => {
              // Handle both camelCase (frontend) and snake_case (backend) field names
              const msgAny = msg as unknown as Record<string, unknown>
              const title = msg.title
              const internalMsgs = (msg.internalMsgs || msgAny.internal_msgs || []) as string[]
              const toolOutputs = (msg.toolOutputs || msgAny.tool_outputs || []) as string[]
              const checkpointMsg = (msg.checkpointMsg || msgAny.checkpoint_msg || '') as string
              const todoList = (msg.todoList || msgAny.todo_list || []) as ProgressMessageTodoItem[]
              const messageType = msg.messageType || msgAny.message_type
              // Handle both string ('TODO_LIST') and numeric (4) message types
              const isTodoListType = messageType === 'TODO_LIST' || messageType === 4
              const hasTodoList = isTodoListType && Array.isArray(todoList) && todoList.length > 0
              const hasDetails =
                (Array.isArray(internalMsgs) && internalMsgs.length > 0) ||
                (Array.isArray(toolOutputs) && toolOutputs.length > 0) ||
                (messageType === 'CHECKPOINT' && checkpointMsg) ||
                hasTodoList
              if (messageType === 'SUMMARY') {
                return null
              }
              const isFinalRegular = !!finalRegularMsgHash && msg.msgHash === finalRegularMsgHash
              return (
                <div
                  key={`progress-msg-${msg.msgHash || msgIndex}`}
                  className={`flex flex-col w-full ${!isThinking ? 'gap-[2px]' : ''} ${hasDetails && !isFinalRegular ? 'pb-[8px]' : ''}`}
                >
                  {/* Message title as header - show unless it's a TODO_LIST with items */}
                  {!hasTodoList && (
                    <div
                      className={`flex flex-col self-stretch justify-center font-semibold leading-normal tracking-[-0.33px] ${isThinking ? 'font-mono text-[11px]' : 'font-sans text-[12px]'} text-tokens-neutral-text-tertiary`}
                    >
                      <ProgressMarkdown text={formatProgressTitle(title)} />
                    </div>
                  )}

                  {/* Todo list - render inline checklist for TODO_LIST messages */}
                  {hasTodoList && <TodoList items={todoList} />}

                  {/* Message details as content - show if available */}
                  {hasDetails && !hasTodoList && (
                    <div className={`flex flex-col font-semibold`}>
                      {messageType === 'CHECKPOINT' && checkpointMsg && (
                        <span className={`font-mono leading-normal`}>{checkpointMsg}</span>
                      )}

                      {Array.isArray(internalMsgs) &&
                        internalMsgs.length > 0 &&
                        internalMsgs.map((detailMsg, detailIndex) =>
                          renderLegacyDetail(detailMsg, detailIndex)
                        )}

                      {Array.isArray(toolOutputs) &&
                        toolOutputs.length > 0 &&
                        toolOutputs.map((output, outputIndex) => (
                          <pre
                            key={`tool-${outputIndex}`}
                            className={`font-medium leading-normal whitespace-pre-wrap ${isThinking ? 'text-tokens-neutral-text-tertiary' : 'text-tokens-neutral-text-quaternary'}`}
                          >
                            {output}
                          </pre>
                        ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export const ChatProgressMessageView: React.FC<ChatProgressMessageViewProps> = ({
  progressMessages,
  isStreaming = false,
  taskContext
}) => {
  const renderThinkingFallback = () => {
    const fallbackMessage: ProgressMessageData = {
      title: 'Thinking...',
      messageType: 'REGULAR',
      msgHash: 'thinking-fallback',
      taskStatus: '',
      internalMsgs: [],
      toolOutputs: [],
      checkpointMsg: '',
      createdTsNs: 0
    }
    return (
      <ChatProgressMessageViewInner
        sortedMessages={[]}
        stateKey='progress-stream-thinking-fallback'
        isStreaming={true}
        hasSummary={false}
        buttonTextMessage={fallbackMessage}
        nonTodoRegularMessages={[]}
        taskContext={taskContext}
      />
    )
  }

  if (!progressMessages || progressMessages.length === 0) {
    if (isStreaming) return renderThinkingFallback()
    return null
  }

  const validMessages = progressMessages.filter(msg => {
    if (!msg || !msg.title) return false
    if (!isStreaming && msg.messageType === 'TODO_LIST') return false
    return true
  })

  if (validMessages.length === 0) {
    if (isStreaming) return renderThinkingFallback()
    return null
  }

  const summaryMessages = validMessages.filter(msg => msg.messageType === 'SUMMARY')
  const regularMessages = validMessages.filter(msg => msg.messageType !== 'SUMMARY')
  const hasSummary = summaryMessages.length > 0

  // CUR-206: with interleaved turns (think -> text -> think -> text), each
  // "think" phase becomes its own ChatProgressMessageView cluster. The agent
  // only emits a SUMMARY ("Worked for Xs") for the WHOLE turn, attached to a
  // single phase (typically the first). The other phase clusters would have
  // no SUMMARY, and a blanket `return null` here made them vanish post-stream
  // — leaving an unfilled vertical gap in the layout where the PMV used to be
  // during streaming.
  //
  // Render rule for post-stream non-SUMMARY clusters: render if the cluster
  // carries information worth showing — either multiple progress messages
  // OR at least one with expandable detail (internalMsgs / toolOutputs /
  // todoList). A lone title-only progress message with nothing under it is
  // visual noise once the stream is done, so we suppress those (React's
  // layout closes the vertical space cleanly when this returns null).
  const hasExpandableDetail = regularMessages.some(msg => {
    const msgAny = msg as unknown as Record<string, unknown>
    const internalMsgs = (msg.internalMsgs ||
      (msgAny.internal_msgs as string[] | undefined) ||
      []) as string[]
    const toolOutputs = (msg.toolOutputs ||
      (msgAny.tool_outputs as string[] | undefined) ||
      []) as string[]
    const todoList = msg.todoList || []
    return internalMsgs.length > 0 || toolOutputs.length > 0 || todoList.length > 0
  })
  if (!isStreaming && !hasSummary && regularMessages.length <= 1 && !hasExpandableDetail)
    return null

  const sortedMessages = [...validMessages].sort((a, b) => {
    const timeA = a.createdTsNs || 0
    const timeB = b.createdTsNs || 0
    return timeA - timeB
  })

  const nonTodoRegularMessages = regularMessages.filter(msg => msg.messageType !== 'TODO_LIST')
  const latestRegularByUpdate = nonTodoRegularMessages.reduce<ProgressMessageData | null>(
    (acc, cur) => {
      if (!acc) return cur
      const accTs = acc.updatedTsNs ?? acc.createdTsNs ?? 0
      const curTs = cur.updatedTsNs ?? cur.createdTsNs ?? 0
      return curTs >= accTs ? cur : acc
    },
    null
  )

  const buttonTextMessage =
    summaryMessages.length > 0 ? summaryMessages[summaryMessages.length - 1] : latestRegularByUpdate

  if (!buttonTextMessage) return null

  const clusterHash = sortedMessages.length > 0 ? sortedMessages[0].msgHash || 'default' : 'default'
  const stateKey = `progress-stream-${clusterHash}`

  return (
    <ChatProgressMessageViewInner
      sortedMessages={sortedMessages}
      stateKey={stateKey}
      isStreaming={isStreaming}
      hasSummary={hasSummary}
      buttonTextMessage={buttonTextMessage}
      nonTodoRegularMessages={nonTodoRegularMessages}
      taskContext={taskContext}
    />
  )
}

export default ChatProgressMessageView
